import { ApiResponseMeta } from '@interfaces/api';
import {
  HotFilterItem,
  HotFiltersFacet,
  HotFiltersFacetsFields,
  HotFiltersPaginationState,
} from '@interfaces/api/responses/hot-filters.response';
import { PageType } from '@interfaces/common/common';
import { convertAppliedHotFiltersToPayload } from './hot-filters-parsing-util';

// Ensures parent items (like categoriesLvl0) come before child items (like categoriesLvl1) for a logical hierarchy.
export const sortHotFiltersObjectKeysAsc = (data: Partial<Record<HotFiltersFacetsFields, HotFiltersFacet[]>>) => {
  const sortedData = {};
  if (data) {
    Object.keys(data)
      .sort()
      .forEach((key) => {
        sortedData[key] = data[key];
      });
    return sortedData;
  }
};

/**
 * Extracts filter IDs and names from URL parameters.
 * `urlParams` with `brand=Gucci#2` will return:
 * `{ "brand": { "name": "Gucci", "id": "2" } }`.
 */
export const extractHotFilterIdsFromUrl = (
  urlParams: URLSearchParams,
): Record<string, { id: string; name: string }[]> => {
  const filterIds = {};
  urlParams.forEach((value, key) => {
    const decodedValue = decodeURIComponent(value);
    const ids = decodedValue.split(/[_>]/).filter(Boolean); // Brand _ / Category >

    // Assuming the value is name#id, we split by '#' to separate them
    filterIds[key] = ids
      .map((id) => {
        const parts = id.split('#');
        return {
          name: parts[0] || null,
          id: parts[1] || null,
        };
      })
      .filter((item) => item.id);
  });

  return filterIds;
};

export const sortCheckedHotFiltersFirst = (filters: HotFilterItem[]): HotFilterItem[] => {
  const sortChildren = (children: HotFilterItem[]): HotFilterItem[] => {
    const allItems: HotFilterItem[] = [];
    const checkedChildren: HotFilterItem[] = [];
    const uncheckedChildren: HotFilterItem[] = [];

    // Classify children into allItems, checkedChildren, and uncheckedChildren
    for (const child of children) {
      const reorderedGrandchildren = sortChildren(child.children); // Recursively reorder grandchildren
      const hasCheckedGrandchild = reorderedGrandchildren.some((grandchild) => grandchild.isChecked);

      if (child.id.startsWith('all_')) {
        allItems.push({ ...child, children: reorderedGrandchildren });
      } else if (child.isChecked || hasCheckedGrandchild) {
        checkedChildren.push({ ...child, children: reorderedGrandchildren });
      } else {
        uncheckedChildren.push({ ...child, children: reorderedGrandchildren });
      }
    }

    // Combine allItems (always first), then checkedChildren, then uncheckedChildren
    return [...allItems, ...checkedChildren, ...uncheckedChildren];
  };

  // Apply sorting to the children of each filter based on isChecked state and special all_ handling
  return filters.map((filter) => ({
    ...filter,
    children: sortChildren(filter.children),
  }));
};

// It recursively searches through `items` and their children to find an item with the matching ID.
export const getHotFilterItemById = (items: HotFilterItem[], id: string | number): HotFilterItem | null => {
  if (!items || !Array.isArray(items)) {
    return null;
  }

  return items.reduce<HotFilterItem | null>((found, item) => {
    if (found) {
      return found;
    }

    if (item.id === id) {
      return item;
    }

    return item.children ? getHotFilterItemById(item.children, id) : null;
  }, null);
};

export const resetHotfiltersItemsToUnchecked = (children: HotFilterItem[]): HotFilterItem[] => {
  return children.map((child) => ({
    ...child,
    isChecked: false,
    children: child.children ? resetHotfiltersItemsToUnchecked(child.children) : [],
  }));
};

export const getAllHotFiltersItemChildren = (items: HotFilterItem[]): HotFilterItem[] =>
  items.flatMap((item) => (item.children ? [item, ...getAllHotFiltersItemChildren(item.children)] : [item]));

// Retrieves the hot filter tabs that match the checked children based on their parent ID.
export const getHotFiltersMatchingTabs = (checkedChildren: HotFilterItem[], tabs: HotFilterItem): HotFilterItem[] => {
  return tabs.children.filter((tc) => checkedChildren.some((ac) => ac.parentID === tc.filterId));
};

// Filters and returns the list of hot filter children that are checked.
export const getHotFiltersCheckedChildren = (children?: HotFilterItem[]): HotFilterItem[] => {
  return children?.filter((child) => child.isChecked) || [];
};

export const getHotFiltersAnalyticDetails = (
  pageType: PageType,
  isUserSlug: boolean,
  eventType: string,
): Record<string, string> => {
  const category: string =
    pageType === 'favourite' || pageType === 'profile-favorites'
      ? isUserSlug
        ? 'my_favourites'
        : 'member_favourites'
      : '';

  const eventName: string =
    pageType === 'favourite' || pageType === 'profile-favorites' ? `favorites_${eventType}` : '';

  return { category, eventName };
};

export const getHotFiltersPaginationState = ({ limit, offset, count }: ApiResponseMeta): HotFiltersPaginationState => {
  if (count <= limit) {
    return {
      currentPage: 0,
      totalPages: 0,
      totalCount: count,
    };
  }

  const totalPages = Math.ceil(count / limit);
  const currentPage = offset / limit + 1;
  return {
    totalPages,
    currentPage,
    totalCount: count,
  };
};

export const getSlugForPageType = (
  pageType: string,
  favoritesUserSlug?: string,
  profileSlug?: string,
  loggedInUserSlug?: string,
): string => {
  const defaultSlug = loggedInUserSlug || '';

  switch (pageType) {
    case 'favourite':
      return favoritesUserSlug?.[0] || defaultSlug;
    case 'profile-favorites':
      return profileSlug?.[0] || defaultSlug;
    default:
      return defaultSlug;
  }
};

export const buildHotFiltersAnalyticPayload = ({
  totalCount,
  sortBy,
  filtersApplied,
  filtersDetails,
  property,
  pageType,
  isUserSlug,
  eventType,
}) => {
  const { category, eventName } = getHotFiltersAnalyticDetails(pageType, isUserSlug, eventType);

  return {
    eventName,
    payload: {
      category,
      nb_items: totalCount?.toString() || '0',
      sorting: sortBy,
      filters_applied: JSON.stringify(convertAppliedHotFiltersToPayload(filtersApplied)),
      filters_details: JSON.stringify(convertAppliedHotFiltersToPayload(filtersDetails)),
      property,
    },
  };
};

/**
 * Calculates the total number of checked items from both nested child items and top-level filtered children.
 *
 * The function retrieves all child items of a given data item, filters out checked child items while excluding
 * those with IDs starting with 'all_'. It also filters checked items from the top-level filtered children that
 * were not included in the checked child items. The final count includes both sets of checked items.
 *
 * @param dataItem - The data item containing nested child items.
 * @returns The total count of checked items.
 */
export const calculateHotFiltersTotalCheckedItems = (dataItem: HotFilterItem): number => {
  const childItems = getAllHotFiltersItemChildren(dataItem?.children || []);

  const checkedChildItems = childItems.flatMap(
    (parentItem) =>
      parentItem.children?.filter((childItem) => childItem.isChecked && childItem.id !== `all_${childItem.parentID}`) ||
      [],
  );

  const remainingCheckedFilteredItems = dataItem?.children.filter(
    (filteredItem) =>
      filteredItem.isChecked &&
      filteredItem.id !== `all_${filteredItem.parentID}` &&
      !checkedChildItems.some((checkedChildItem) => checkedChildItem.parentID === filteredItem.id),
  );

  return remainingCheckedFilteredItems.length + checkedChildItems.length;
};
